import { HttpErrorResponse } from '@angular/common/http';

export type SignedOutReason = 'logout' | 'expired' | 'error';

export const SIGNED_OUT_PATH = '/signed-out';

const REASONS: readonly SignedOutReason[] = ['logout', 'expired', 'error'];

export function parseSignedOutReason(value: string | null | undefined): SignedOutReason {
  return REASONS.find(r => r === value) ?? 'logout';
}

export function isSignedOutUrl(url: string): boolean {
  return url.split(/[?#]/)[0] === SIGNED_OUT_PATH;
}

// Only same-origin paths: a protocol-relative (//host) or backslash (/\host) value would let the query param redirect off-site.
export function safeReturnPath(value: string | null | undefined): string | null {
  if (!value || !value.startsWith('/') || value[1] === '/' || value[1] === '\\') {
    return null;
  }
  return isSignedOutUrl(value) ? null : value;
}

// Mirrors auth0-angular's shouldHandleCallback: the SDK is mid-callback when state and (code or error) are present.
export function isAuthCallbackSearch(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.has('state') && (params.has('code') || params.has('error'));
}

export function signedOutUrl(reason: SignedOutReason, returnTo?: string | null): string {
  const params = new URLSearchParams({ reason });
  const safe = safeReturnPath(returnTo);
  if (safe) {
    params.set('returnTo', safe);
  }
  return `${SIGNED_OUT_PATH}?${params.toString()}`;
}

// A 401 or an Auth0 SDK token failure (login_required, missing_refresh_token, invalid_grant, ...) means the session is gone.
export function isSessionError(err: unknown): boolean {
  if (err instanceof HttpErrorResponse) {
    return err.status === 401;
  }
  return typeof err === 'object' && err !== null
    && typeof (err as { error?: unknown }).error === 'string'
    && 'error_description' in err;
}
